import React from 'react'
import { Chip, Button, CircularProgress, Skeleton, useTheme, useMediaQuery } from '@mui/material'
import { VisibilityOutlined as ViewDetailsIcon, EditOutlined as EditIcon } from '@mui/icons-material'
import { SMBData } from '../../../services/api'
import PageBanner from '../../../components/PageBanner'
import { PAGE_BANNER_PRIMARY_BTN_SX, PAGE_BANNER_OUTLINED_BTN_SX } from '../../../theme/constants'
import { usePaymentContext } from '../../../contexts/PaymentContext'

interface DashboardHeaderProps {
  smb: SMBData | null;
  loading?: boolean;
  isScraping: boolean;
  summaryLoading: boolean;
  handleViewDetails: () => void;
  setEditOpen: (open: boolean) => void;
  loadSummary: (smbId: string) => void;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  smb,
  loading = false,
  isScraping,
  summaryLoading,
  handleViewDetails,
  setEditOpen,
  loadSummary,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { hasAnySubscription } = usePaymentContext()

  const isLoading = loading || !smb

  return (
    <PageBanner
      title={
        isLoading ? (
          <Skeleton variant="text" width={180} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
        ) : (
          smb?.display_name || smb?.name || ''
        )
      }
      subtitle="Manage your business and growth strategies"
      statusChip={
        isLoading ? (
          <Skeleton variant="rounded" width={64} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '10px' }} />
        ) : (
          <Chip
            label={hasAnySubscription ? 'Active' : 'Inactive'}
            size="small"
            sx={{
              height: 20,
              fontSize: '0.75rem',
              fontWeight: 700,
              bgcolor: '#fff',
              color: hasAnySubscription ? '#16a34a' : '#dc2626',
              border: '1px solid',
              borderColor: 'rgba(255,255,255,0.8)',
            }}
          />
        )
      }
      action={
        isScraping ? (
          <Chip
            icon={<CircularProgress size={14} color="inherit" />}
            label="Processing..."
            color="primary"
            variant="outlined"
            sx={{
              bgcolor: 'rgba(255,255,255,0.1)',
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.2)',
              fontWeight: 600,
            }}
          />
        ) : (
          <>
            <Button
              variant="outlined"
              startIcon={<ViewDetailsIcon />}
              onClick={handleViewDetails}
              disabled={isLoading || summaryLoading}
              sx={PAGE_BANNER_OUTLINED_BTN_SX}
            >
              {isMobile ? 'View' : 'View Details'}
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditOpen(true)}
              disabled={isLoading || summaryLoading}
              sx={PAGE_BANNER_PRIMARY_BTN_SX}
            >
              {isMobile ? 'Edit' : 'Edit Business'}
            </Button>
          </>
        )
      }
      onRefresh={() => smb?.id && loadSummary(smb.id)}
      refreshing={summaryLoading}
    />
  )
}
